import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import { toBlob } from "html-to-image";
import { api } from "../../lib/apiClient";
import { toast } from "sonner";
import { CURRENCIES, formatMoney } from "../../lib/currencies";
import { Loader2, Plus, Trash2, Pencil, FileText, Image as ImageIcon, MessageCircle, Mail, X, Upload, Download, Search as SearchIcon, Eye } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TAX_PRESETS = ["VAT", "GST", "Sales Tax", "HST"];
const PAYMENT_METHODS = ["Cash", "Bank transfer", "Card", "Cheque", "Online / payment link", "Other"];
const statusColor = { draft: "#71717a", sent: "#3b82f6", paid: "#22c55e" };
const intlPhone = (p) => { let d = (p || "").replace(/\D/g, ""); if (d.startsWith("0")) d = "44" + d.slice(1); return d; };

const blankItem = () => ({ description: "", quantity: "1", unit_price: "" });

export default function Invoices() {
  const { company, reloadCompany } = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [customTax, setCustomTax] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [brandOpen, setBrandOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [formPreview, setFormPreview] = useState(null);
  const previewRef = useRef(null);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { if (company?.is_pro) load(); }, []); // eslint-disable-line

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openNew = async (prefill) => {
    let number = "";
    try { number = (await api.get("/invoices/next-number")).data.number; } catch { /* ignore */ }
    setEditing(null);
    setCustomTax(false);
    setForm({
      number, invoice_date: new Date().toISOString().slice(0, 10), due_date: "",
      customer_name: prefill?.customer_name || "", customer_email: prefill?.customer_email || "",
      customer_phone: prefill?.customer_phone || "", customer_address: prefill?.address || "",
      currency: company?.display_currency || "GBP", tax_label: "", tax_rate: "",
      items: prefill?.description ? [{ description: prefill.description, quantity: "1", unit_price: "" }] : [blankItem()],
      notes: "", payment_method: "", payment_details: "",
    });
    setEditorOpen(true);
  };

  // Pre-fill an invoice when arriving from a completed booking ("Create invoice")
  useEffect(() => {
    const pj = location.state?.prefillJob;
    if (pj && company?.is_pro) {
      openNew(pj);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]); // eslint-disable-line

  const openEdit = (inv) => {
    setEditing(inv);
    setCustomTax(!!inv.tax_label && !TAX_PRESETS.includes(inv.tax_label));
    setForm({
      number: inv.number || "", invoice_date: inv.invoice_date || "", due_date: inv.due_date || "",
      customer_name: inv.customer_name || "", customer_email: inv.customer_email || "",
      customer_phone: inv.customer_phone || "", customer_address: inv.customer_address || "",
      currency: inv.currency || "GBP", tax_label: inv.tax_label || "", tax_rate: inv.tax_rate || "",
      items: (inv.items?.length ? inv.items : [blankItem()]).map((i) => ({
        description: i.description || "", quantity: String(i.quantity ?? "1"), unit_price: String(i.unit_price ?? ""),
      })),
      notes: inv.notes || "",
      payment_method: inv.payment_method || "", payment_details: inv.payment_details || "",
    });
    setEditorOpen(true);
  };

  const updateItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, idx) => idx !== i) : f.items }));

  const totals = () => {
    const sub = (form?.items || []).reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
    const rate = parseFloat(form?.tax_rate) || 0;
    const tax = sub * rate / 100;
    return { sub, tax, total: sub + tax };
  };

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error("Enter the customer's name"); return; }
    const items = form.items
      .filter((it) => it.description.trim() !== "" || it.unit_price !== "")
      .map((it) => ({ description: it.description.trim(), quantity: parseFloat(it.quantity) || 0, unit_price: parseFloat(it.unit_price) || 0 }));
    if (items.length === 0) { toast.error("Add at least one line item"); return; }
    const body = {
      number: form.number.trim() || null,
      customer_name: form.customer_name.trim(), customer_email: form.customer_email.trim() || null,
      customer_phone: form.customer_phone.trim() || null, customer_address: form.customer_address.trim() || null,
      business_name: company?.name, business_address: company?.invoice_address || null,
      currency: form.currency, tax_label: form.tax_label.trim() || null, tax_rate: parseFloat(form.tax_rate) || 0,
      items, notes: form.notes.trim() || null, invoice_date: form.invoice_date || null, due_date: form.due_date || null,
      payment_method: form.payment_method || null, payment_details: form.payment_details.trim() || null,
      status: editing?.status || "draft",
    };
    setSaving(true);
    try {
      const res = editing ? await api.patch(`/invoices/${editing.id}`, body) : await api.post("/invoices", body);
      toast.success(editing ? "Invoice updated" : "Invoice created");
      setEditorOpen(false);
      await load();
      setPreview(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save invoice"); }
    finally { setSaving(false); }
  };

  const openFormPreview = () => {
    if (!form.customer_name.trim()) { toast.error("Enter the customer's name to preview"); return; }
    const round2 = (n) => Math.round(n * 100) / 100;
    const items = form.items
      .filter((it) => it.description.trim() !== "" || it.unit_price !== "")
      .map((it) => {
        const qty = parseFloat(it.quantity) || 0, price = parseFloat(it.unit_price) || 0;
        return { description: it.description.trim(), quantity: qty, unit_price: price, line_total: round2(qty * price) };
      });
    const t = totals();
    setFormPreview({
      number: form.number || "(draft)", invoice_date: form.invoice_date, due_date: form.due_date,
      customer_name: form.customer_name.trim(), customer_email: form.customer_email.trim() || null,
      customer_phone: form.customer_phone.trim() || null, customer_address: form.customer_address.trim() || null,
      business_name: company?.name, business_address: company?.invoice_address || null,
      currency: form.currency, tax_label: form.tax_label.trim() || null, tax_rate: parseFloat(form.tax_rate) || 0,
      items, subtotal: round2(t.sub), tax_amount: round2(t.tax), total: round2(t.total),
      notes: form.notes.trim() || null, payment_method: form.payment_method || null, payment_details: form.payment_details.trim() || null,
    });
  };

  const removeInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number}? This can't be undone.`)) return;
    await api.delete(`/invoices/${inv.id}`);
    toast.success("Invoice deleted");
    load();
  };

  const markSent = async (inv) => {
    if (inv.status === "draft") {
      try { await api.patch(`/invoices/${inv.id}/status`, { status: "sent" }); load(); setPreview((p) => p ? { ...p, status: "sent" } : p); } catch { /* ignore */ }
    }
  };
  const setStatus = async (inv, status) => {
    await api.patch(`/invoices/${inv.id}/status`, { status });
    setPreview((p) => p ? { ...p, status } : p);
    load();
    toast.success(`Marked ${status}`);
  };

  const makeBlob = async () => {
    if (!previewRef.current) return null;
    return await toBlob(previewRef.current, { pixelRatio: 2, backgroundColor: "#ffffff", cacheBust: true, skipFonts: true });
  };

  const shareImage = async (inv, channel) => {
    try {
      const blob = await makeBlob();
      if (!blob) return;
      const file = new File([blob], `${(inv.number || "invoice").replace(/\W+/g, "_")}.png`, { type: "image/png" });
      const text = `Invoice ${inv.number} from ${inv.business_name || company?.name || ""} — ${formatMoney(inv.total, inv.currency)}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Invoice ${inv.number}`, text });
        markSent(inv);
        return;
      }
      // Desktop fallback: download the image + open the channel with a text message
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = file.name; a.click(); URL.revokeObjectURL(url);
      if (channel === "whatsapp") window.open(`https://wa.me/${intlPhone(inv.customer_phone)}?text=${encodeURIComponent(text)}`, "_blank");
      else if (channel === "email") window.location.href = `mailto:${inv.customer_email || ""}?subject=${encodeURIComponent("Invoice " + inv.number)}&body=${encodeURIComponent(text)}`;
      toast.success("Invoice image saved — attach it to your message");
      markSent(inv);
    } catch { toast.error("Could not generate the invoice image"); }
  };

  const saveImage = async (inv) => {
    try {
      const blob = await makeBlob();
      if (!blob) return;
      const file = new File([blob], `${(inv.number || "invoice").replace(/\W+/g, "_")}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Invoice ${inv.number}` }); // lets user "Save Image" to Photos
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = file.name; a.click(); URL.revokeObjectURL(url);
      toast.success("Invoice image downloaded");
    } catch { toast.error("Could not generate the invoice image"); }
  };

  if (!company?.is_pro) {
    return (
      <div className="max-w-lg mx-auto text-center py-16" data-testid="invoices-pro-gate">
        <div className="h-14 w-14 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-5"><FileText className="h-6 w-6 text-primary" /></div>
        <h1 className="font-display text-4xl tracking-tight mb-3">INVOICING IS A <span className="text-primary">PRO</span> FEATURE</h1>
        <p className="text-muted-foreground mb-2">Create professional invoices, add your logo &amp; tax, and send them straight to customers over WhatsApp or email as an image.</p>
        <p className="text-muted-foreground mb-8">It's included in the <span className="text-foreground font-medium">Pro plan (£75.99/mo)</span>.</p>
        <Button data-testid="upgrade-to-pro-btn" onClick={() => navigate("/dashboard/billing")} className="rounded-sm bg-primary hover:bg-orange-600 text-white h-12 px-8 text-base">Upgrade to Pro</Button>
      </div>
    );
  }

  if (!invoices) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const q = query.trim().toLowerCase();
  const filtered = !q ? invoices : invoices.filter((inv) => [
    inv.number, inv.customer_name, inv.invoice_date, inv.due_date, inv.status,
    inv.customer_phone, inv.customer_email, String(inv.total),
  ].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">INVOICES</h1>
          <p className="text-muted-foreground mt-1">Create an invoice and send it as an image over WhatsApp or email, or save it to your photos.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="invoice-branding-btn" onClick={() => setBrandOpen(true)} variant="outline" className="rounded-sm border-white/10 gap-2"><ImageIcon className="h-4 w-4" /> Logo & details</Button>
          <Button data-testid="new-invoice-btn" onClick={() => openNew()} className="rounded-sm bg-primary hover:bg-orange-600 text-white gap-2"><Plus className="h-4 w-4" /> New invoice</Button>
        </div>
      </div>

      {invoices.length > 0 && (
        <div className="relative max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="invoice-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, number, date, status…" className="inp pl-9" />
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-12 text-center text-muted-foreground">
          <FileText className="h-8 w-8 mx-auto mb-3 opacity-50" />
          No invoices yet. Tap <span className="text-primary">New invoice</span> to create your first one.
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-10 text-center text-muted-foreground" data-testid="invoice-no-results">
          No invoices match "{query}".
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((inv) => (
            <div key={inv.id} data-testid={`invoice-card-${inv.id}`} className="bg-[#18181b] border border-white/10 rounded-sm p-5 hover:border-primary/40 transition-[border-color] duration-200">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm text-muted-foreground">{inv.number}</span>
                <span className="text-xs uppercase tracking-wider font-semibold px-2.5 py-1 rounded-sm capitalize" style={{ backgroundColor: `${statusColor[inv.status]}22`, color: statusColor[inv.status] }}>{inv.status}</span>
              </div>
              <p className="font-medium truncate">{inv.customer_name}</p>
              <p className="text-xs text-muted-foreground mb-3">{inv.invoice_date}</p>
              <p className="font-display text-2xl tracking-tight text-primary mb-4">{formatMoney(inv.total, inv.currency)}</p>
              <div className="flex gap-2">
                <Button data-testid={`invoice-share-${inv.id}`} onClick={() => setPreview(inv)} className="flex-1 rounded-sm bg-primary hover:bg-orange-600 text-white gap-1.5 text-xs">Send</Button>
                <Button data-testid={`invoice-edit-${inv.id}`} onClick={() => openEdit(inv)} variant="outline" size="icon" className="rounded-sm border-white/10"><Pencil className="h-4 w-4" /></Button>
                <Button data-testid={`invoice-delete-${inv.id}`} onClick={() => removeInvoice(inv)} variant="outline" size="icon" className="rounded-sm border-white/10 text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-editor">
          <DialogTitle className="font-display text-2xl tracking-tight font-normal">{editing ? "EDIT INVOICE" : "NEW INVOICE"}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground -mt-2">Fill in the details — the totals calculate automatically.</DialogDescription>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="lbl">Invoice number</Label><Input data-testid="inv-number" value={form.number} onChange={setF("number")} className="inp" /></div>
                <div><Label className="lbl">Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                    <SelectTrigger data-testid="inv-currency" className="inp"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-white/10 text-foreground max-h-72">
                      {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.symbol} {c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="lbl">Invoice date</Label><Input data-testid="inv-date" type="date" value={form.invoice_date} onChange={setF("invoice_date")} className="inp" /></div>
                <div><Label className="lbl">Due date (optional)</Label><Input data-testid="inv-due" type="date" value={form.due_date} onChange={setF("due_date")} className="inp" /></div>
              </div>

              <div className="border-t border-white/10 pt-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Bill to</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="lbl">Customer name</Label><Input data-testid="inv-cust-name" value={form.customer_name} onChange={setF("customer_name")} placeholder="e.g. John Smith" className="inp" /></div>
                  <div><Label className="lbl">Phone</Label><Input data-testid="inv-cust-phone" value={form.customer_phone} onChange={setF("customer_phone")} className="inp" /></div>
                  <div><Label className="lbl">Email</Label><Input data-testid="inv-cust-email" value={form.customer_email} onChange={setF("customer_email")} className="inp" /></div>
                  <div><Label className="lbl">Address</Label><Input data-testid="inv-cust-address" value={form.customer_address} onChange={setF("customer_address")} className="inp" /></div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Items — product / service</p>
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="flex items-end gap-2" data-testid={`inv-item-${i}`}>
                      <div className="flex-1"><Input data-testid={`inv-item-desc-${i}`} value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder="Description (e.g. Bathroom re-tile / Paint 10L)" className="inp" /></div>
                      <div className="w-16"><Input data-testid={`inv-item-qty-${i}`} type="number" inputMode="decimal" min="0" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} placeholder="Qty" className="inp" /></div>
                      <div className="w-24"><Input data-testid={`inv-item-price-${i}`} type="number" inputMode="decimal" min="0" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} placeholder="Price" className="inp" /></div>
                      <Button data-testid={`inv-item-remove-${i}`} onClick={() => removeItem(i)} variant="ghost" size="icon" className="rounded-sm text-muted-foreground hover:text-red-400 shrink-0"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
                <Button data-testid="inv-add-item" onClick={addItem} variant="outline" className="rounded-sm border-white/10 gap-2 mt-2"><Plus className="h-4 w-4" /> Add item</Button>
              </div>

              <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-3">
                <div><Label className="lbl">Tax</Label>
                  <Select value={form.tax_label && !customTax ? form.tax_label : (customTax ? "Other" : "none")}
                    onValueChange={(v) => {
                      if (v === "none") { setCustomTax(false); setForm((f) => ({ ...f, tax_label: "", tax_rate: "" })); }
                      else if (v === "Other") { setCustomTax(true); setForm((f) => ({ ...f, tax_label: "" })); }
                      else { setCustomTax(false); setForm((f) => ({ ...f, tax_label: v })); }
                    }}>
                    <SelectTrigger data-testid="inv-tax-type" className="inp"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-white/10 text-foreground">
                      <SelectItem value="none">No tax</SelectItem>
                      {TAX_PRESETS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      <SelectItem value="Other">Other…</SelectItem>
                    </SelectContent>
                  </Select>
                  {customTax && <Input data-testid="inv-tax-label" value={form.tax_label} onChange={setF("tax_label")} placeholder="Tax name" className="inp mt-2" />}
                </div>
                <div><Label className="lbl">Tax rate %</Label>
                  <Input data-testid="inv-tax-rate" type="number" inputMode="decimal" min="0" value={form.tax_rate}
                    onChange={setF("tax_rate")} disabled={!form.tax_label && !customTax} placeholder="0" className="inp" />
                </div>
              </div>

              <div><Label className="lbl">Notes / terms (optional)</Label>
                <Textarea data-testid="inv-notes" value={form.notes} onChange={setF("notes")} rows={2} placeholder="e.g. Payment due within 14 days · Thank you for your business!" className="inp resize-none" />
              </div>

              <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-3">
                <div><Label className="lbl">Payment method</Label>
                  <Select value={form.payment_method || "none"} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="inv-pay-method" className="inp"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-white/10 text-foreground">
                      <SelectItem value="none">Not specified</SelectItem>
                      {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="lbl">Payment details (optional)</Label>
                  <Input data-testid="inv-pay-details" value={form.payment_details} onChange={setF("payment_details")} placeholder="e.g. Sort 12-34-56 · Acc 12345678 · or 'Cash on completion'" className="inp" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Quick pick:</span>
                {["Cash", "Bank transfer", "Card"].map((m) => (
                  <button key={m} type="button" data-testid={`pay-chip-${m.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setForm((f) => ({ ...f, payment_method: m }))}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.payment_method === m ? "bg-primary text-white border-primary" : "border-white/15 text-muted-foreground hover:text-foreground"}`}>{m}</button>
                ))}
              </div>

              <div className="bg-[#18181b] border border-primary/40 rounded-sm p-4 space-y-1.5">
                <Row label="Subtotal" value={formatMoney(totals().sub, form.currency)} />
                {(form.tax_label || customTax) && <Row label={`${form.tax_label || "Tax"} (${parseFloat(form.tax_rate) || 0}%)`} value={formatMoney(totals().tax, form.currency)} />}
                <div className="flex items-center justify-between pt-1 border-t border-white/10"><span className="font-medium">Total</span><span data-testid="inv-total" className="font-display text-2xl text-primary">{formatMoney(totals().total, form.currency)}</span></div>
              </div>

              <div className="flex gap-2">
                <Button data-testid="inv-preview-btn" onClick={openFormPreview} variant="outline" className="flex-1 rounded-sm border-white/10 gap-2"><Eye className="h-4 w-4" /> Preview</Button>
                <Button data-testid="inv-save-btn" onClick={save} disabled={saving} className="flex-1 rounded-sm bg-primary hover:bg-orange-600 text-white gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save changes" : "Create invoice")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview + share */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invoice-preview-dialog">
          <DialogTitle className="font-display text-2xl tracking-tight font-normal">SEND INVOICE</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground -mt-2">Tap a button to share the invoice as an image, or save it to your photos.</DialogDescription>
          {preview && (
            <>
              <div className="overflow-x-auto rounded-sm border border-white/10">
                <InvoicePreview ref={previewRef} inv={preview} logo={company?.invoice_logo} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button data-testid="share-whatsapp" onClick={() => shareImage(preview, "whatsapp")} className="rounded-sm bg-[#25d366] hover:bg-[#1fb855] text-white gap-1.5 text-xs"><MessageCircle className="h-4 w-4" /> WhatsApp</Button>
                <Button data-testid="share-email" onClick={() => shareImage(preview, "email")} className="rounded-sm bg-primary hover:bg-orange-600 text-white gap-1.5 text-xs"><Mail className="h-4 w-4" /> Email</Button>
                <Button data-testid="save-image" onClick={() => saveImage(preview)} variant="outline" className="rounded-sm border-white/10 gap-1.5 text-xs"><Download className="h-4 w-4" /> Save image</Button>
              </div>
              <p className="text-xs text-muted-foreground text-center -mt-1">On your phone this opens the share sheet — pick WhatsApp, Mail, or "Save Image" for your photos.</p>
              <div className="flex items-center justify-center gap-2 pt-1">
                <span className="text-xs text-muted-foreground">Status:</span>
                {["draft", "sent", "paid"].map((s) => (
                  <button key={s} data-testid={`inv-status-${s}`} onClick={() => setStatus(preview, s)}
                    className={`text-xs px-2.5 py-1 rounded-sm capitalize transition-colors ${preview.status === s ? "text-white" : "text-muted-foreground hover:text-foreground"}`}
                    style={preview.status === s ? { backgroundColor: statusColor[s] } : { backgroundColor: "#26262b" }}>{s}</button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Branding */}
      <BrandingDialog open={brandOpen} onClose={() => setBrandOpen(false)} company={company} reloadCompany={reloadCompany} />

      {/* Live preview of the current form (before saving) */}
      <Dialog open={!!formPreview} onOpenChange={(o) => !o && setFormPreview(null)}>
        <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invoice-form-preview">
          <DialogTitle className="font-display text-2xl tracking-tight font-normal">PREVIEW</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground -mt-2">This is exactly how your customer will see it.</DialogDescription>
          {formPreview && (
            <div className="overflow-x-auto rounded-sm border border-white/10">
              <InvoicePreview inv={formPreview} logo={company?.invoice_logo} />
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center">Close to keep editing, or hit "Create invoice" to send it.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm text-muted-foreground"><span>{label}</span><span className="text-foreground">{value}</span></div>
);

const InvoicePreview = React.forwardRef(({ inv, logo }, ref) => (
  <div ref={ref} className="bg-white text-neutral-900 w-[600px] p-8" style={{ fontFamily: "Inter, Arial, sans-serif" }}>
    <div className="flex items-start justify-between mb-6">
      <div className="max-w-[55%]">
        {logo ? <img src={logo} alt="" crossOrigin="anonymous" className="max-h-16 mb-2 object-contain" /> : null}
        <p className="font-bold text-lg leading-tight">{inv.business_name || "Your business"}</p>
        {inv.business_address && <p className="text-xs text-neutral-500 whitespace-pre-line mt-1">{inv.business_address}</p>}
      </div>
      <div className="text-right">
        <p className="text-3xl font-extrabold tracking-tight" style={{ color: "#f97316" }}>INVOICE</p>
        <p className="text-sm font-mono mt-1">{inv.number}</p>
        <p className="text-xs text-neutral-500 mt-1">Date: {inv.invoice_date}</p>
        {inv.due_date && <p className="text-xs text-neutral-500">Due: {inv.due_date}</p>}
      </div>
    </div>

    <div className="mb-5">
      <p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Bill to</p>
      <p className="font-semibold">{inv.customer_name}</p>
      {inv.customer_address && <p className="text-xs text-neutral-500 whitespace-pre-line">{inv.customer_address}</p>}
      <p className="text-xs text-neutral-500">{[inv.customer_phone, inv.customer_email].filter(Boolean).join(" · ")}</p>
    </div>

    <table className="w-full text-sm mb-4" style={{ borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ borderBottom: "2px solid #111" }} className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
          <th className="py-2">Description</th>
          <th className="py-2 text-right w-14">Qty</th>
          <th className="py-2 text-right w-24">Unit</th>
          <th className="py-2 text-right w-28">Amount</th>
        </tr>
      </thead>
      <tbody>
        {(inv.items || []).map((it, i) => (
          <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
            <td className="py-2 pr-2">{it.description || "—"}</td>
            <td className="py-2 text-right">{it.quantity}</td>
            <td className="py-2 text-right">{formatMoney(it.unit_price, inv.currency)}</td>
            <td className="py-2 text-right">{formatMoney(it.line_total ?? (it.quantity * it.unit_price), inv.currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>

    <div className="flex justify-end">
      <div className="w-56 text-sm">
        <div className="flex justify-between py-1"><span className="text-neutral-500">Subtotal</span><span>{formatMoney(inv.subtotal, inv.currency)}</span></div>
        {inv.tax_label && <div className="flex justify-between py-1"><span className="text-neutral-500">{inv.tax_label} ({inv.tax_rate}%)</span><span>{formatMoney(inv.tax_amount, inv.currency)}</span></div>}
        <div className="flex justify-between py-2 mt-1 font-bold text-base" style={{ borderTop: "2px solid #111" }}><span>Total</span><span style={{ color: "#f97316" }}>{formatMoney(inv.total, inv.currency)}</span></div>
      </div>
    </div>

    {(inv.payment_method || inv.payment_details) && (
      <div className="mt-5 pt-3" style={{ borderTop: "1px solid #eee" }}>
        <p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Payment</p>
        <p className="text-xs text-neutral-700">{[inv.payment_method, inv.payment_details].filter(Boolean).join(" · ")}</p>
      </div>
    )}

    {inv.notes && (
      <div className="mt-4 pt-3" style={{ borderTop: "1px solid #eee" }}>
        <p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Notes</p>
        <p className="text-xs text-neutral-600 whitespace-pre-line">{inv.notes}</p>
      </div>
    )}
  </div>
));

function BrandingDialog({ open, onClose, company, reloadCompany }) {
  const [logo, setLogo] = useState(company?.invoice_logo || "");
  const [address, setAddress] = useState(company?.invoice_address || "");
  const [prefix, setPrefix] = useState(company?.invoice_prefix || "INV-");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setLogo(company?.invoice_logo || ""); setAddress(company?.invoice_address || ""); setPrefix(company?.invoice_prefix || "INV-"); }
  }, [open, company]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 400;
        let { width, height } = img;
        if (width > max || height > max) {
          const r = Math.min(max / width, max / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        setLogo(canvas.toDataURL("image/png"));
      };
      img.onerror = () => toast.error("Couldn't read that image — try a PNG or JPG");
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company/me", { invoice_logo: logo || null, invoice_address: address.trim() || null, invoice_prefix: prefix.trim() || "INV-" });
      await reloadCompany?.();
      toast.success("Invoice details saved");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground rounded-sm max-w-md" data-testid="branding-dialog">
        <DialogTitle className="font-display text-2xl tracking-tight font-normal">LOGO & DETAILS</DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground -mt-2">These appear on every invoice you create.</DialogDescription>
        <div className="space-y-4">
          <div>
            <Label className="lbl">Business logo</Label>
            <div className="flex items-center gap-3">
              {logo ? <img src={logo} alt="" className="h-14 w-14 object-contain bg-white rounded-sm p-1" /> : <div className="h-14 w-14 rounded-sm bg-secondary flex items-center justify-center text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>}
              <label className="cursor-pointer">
                <input data-testid="logo-upload" type="file" accept="image/*" onChange={onFile} className="hidden" />
                <span className="inline-flex items-center gap-2 text-sm border border-white/10 rounded-sm px-3 py-2 hover:bg-secondary"><Upload className="h-4 w-4" /> Upload</span>
              </label>
              {logo && <Button onClick={() => setLogo("")} variant="ghost" size="icon" className="text-muted-foreground hover:text-red-400"><X className="h-4 w-4" /></Button>}
            </div>
          </div>
          <div><Label className="lbl">Business name & address</Label>
            <Textarea data-testid="brand-address" value={address} onChange={(e) => setAddress(e.target.value)} rows={3} placeholder={`${company?.name || "Your business"}\n123 High Street\nTown, Postcode`} className="inp resize-none" />
          </div>
          <div><Label className="lbl">Invoice number prefix</Label><Input data-testid="brand-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="INV-" className="inp" /></div>
          <Button data-testid="brand-save" onClick={save} disabled={saving} className="w-full rounded-sm bg-primary hover:bg-orange-600 text-white gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save details"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
